// Home (Chris, 2026-10-15): the signed-in person's own work, time off and pay. In order: what needs an
// admin's attention (admins only), this week's time card with its gaps, time off and pay dates, the
// next 4 weeks, the year to date against plan, and their pay with its full cost. Everyone sees their
// own pay; bonus progress is TBD until it can be measured through the year.

import { Body1, Button, Caption1, Card, Link, Subtitle2 } from "@fluentui/react-components";
import { BUCKET_LABEL, isFlatPay, statusOf, type BalanceState, type PayType, type TimeOffBucket } from "../../../core";
import { useApi } from "../api";
import { useCodeLabels } from "../codes";
import { money, pct, perHour, stamp, weekdayDay } from "../format";
import { go, href } from "../router";
import { ErrorBar, Kpi, Loading, useMe } from "../ui";

interface HomeResponse {
  person: { id: string; name: string; title: string | null } | null;
  today?: string;
  weeks?: {
    monday: string;
    state: "open" | "frozen";
    cutoff: string;
    days: { day: string; reported: number; expected: number; holiday: boolean; off: number; gap: boolean }[];
    reported: number;
    expected: number;
    gapDays: number;
    uncategorized: number;
    toReview: number;
  }[];
  timeOff?: { vacation: BalanceState; personal: BalanceState; upcoming: { bucket: TimeOffBucket; from: string; to: string; hours: number }[]; pending: number } | null;
  payday?: string | null;
  nextHolidays?: { day: string; name: string }[];
  ytd?: {
    from: string;
    planned: number;
    reported: number;
    onProjects: number;
    expected: number;
    billableUtilization: number | null;
    projects: { code: string; name: string; billable: boolean; planned: number; reported: number }[];
  };
  ahead?: { weekStarts: string[]; planned: number[]; capacity: number[]; projects: { code: string; name: string; hours: number[] }[]; thresholds: { over: number; under: number } };
  pay?: {
    type: string;
    pay: number;
    taxes: number;
    costs: number;
    bonusTarget: number;
    bonusTaxes: number;
    totalBase: number;
    totalOte: number;
    components: { key: string; label: string; group: "base" | "bonus"; annual: number }[];
    baseRate: number;
    oteRate: number;
    actualRateYtd: number | null;
    costYtd: number;
  } | null;
  attention: { text: string; link: string[] }[] | null;
}

const h = (n: number) => `${Math.round(n * 10) / 10} h`;
const daysUntil = (from: string, to: string) => Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);

export function Home() {
  const me = useMe();
  const data = useApi<HomeResponse>("/home");
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  return (
    <div className="stack home">
      {d.attention && d.attention.length > 0 && <Attention items={d.attention} />}
      {!d.person ? (
        <Card className="panel">
          <Subtitle2>Welcome, {me.name}.</Subtitle2>
          <Body1>
            Your account ({me.email}) isn't matched to a person in the app yet, so there is no time card, time off or pay to show. An admin can add your email
            on your People card.
          </Body1>
        </Card>
      ) : (
        <>
          <div className="home-head">
            <Subtitle2>
              {d.person.name}
              {d.person.title ? `, ${d.person.title}` : ""}
            </Subtitle2>
            <Caption1 className="muted">{weekdayDay(d.today!)}</Caption1>
          </div>
          <div className="card-grid">
            <TimeCardCard weeks={d.weeks!} today={d.today!} />
            <TimeOffCard d={d} />
          </div>
          <div className="card-grid">
            <AheadCard ahead={d.ahead!} />
            <YtdCard ytd={d.ytd!} />
          </div>
          {d.pay && <PayCard pay={d.pay} />}
        </>
      )}
      <Caption1 className="muted">
        Signed in as {me.email} · {me.levels.map((l) => l.name).join(", ")}
      </Caption1>
    </div>
  );
}

function Attention({ items }: { items: { text: string; link: string[] }[] }) {
  return (
    <Card className="panel wide attention">
      <Subtitle2>Needs your attention</Subtitle2>
      <ul className="checklist">
        {items.map((i) => (
          <li key={i.text}>
            <Link href={href(...i.link)}>{i.text}</Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** This week, and last week while it's still open: hours by day against what's expected, and gaps. */
function TimeCardCard({ weeks, today }: { weeks: NonNullable<HomeResponse["weeks"]>; today: string }) {
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>Time card</Subtitle2>
        <Button size="small" onClick={() => go("actuals", "timecards")}>
          Open time card
        </Button>
      </div>
      {weeks.map((w) => {
        const cutoff = stamp(w.cutoff, "America/Chicago");
        const last = w.monday < weeks.at(-1)!.monday;
        return (
          <div key={w.monday} className="home-week">
            <Caption1 className="block">
              <strong>{last ? "Last week" : "This week"}</strong>, {w.monday}: {h(w.reported)} of {h(w.expected)} expected.{" "}
              {w.state === "open" ? `Open until ${cutoff} Central.` : "Locked."}
            </Caption1>
            <table className="edit-table compact home-days">
              <thead>
                <tr>
                  {w.days.map((x) => (
                    <th key={x.day} className="num">
                      {weekdayDay(x.day).slice(0, 3)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {w.days.map((x) => (
                    <td key={x.day} className={`num${x.gap ? " home-gap" : ""}`} title={x.holiday ? "Company holiday" : x.off ? `${h(x.off)} time off` : `${h(x.expected)} expected`}>
                      {x.day > today ? "" : h(x.reported)}
                      {x.holiday && <Caption1 className="block muted">holiday</Caption1>}
                      {!x.holiday && x.off > 0 && <Caption1 className="block muted">off</Caption1>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            {(w.gapDays > 0 || w.uncategorized > 0 || w.toReview > 0) && (
              <Caption1 className="block warn-text">
                {[
                  w.gapDays ? `${w.gapDays} ${w.gapDays === 1 ? "day" : "days"} with less time than expected: add it in Outlook, then resync` : "",
                  w.uncategorized ? `${w.uncategorized} ${w.uncategorized === 1 ? "event" : "events"} with no category` : "",
                  w.toReview > w.uncategorized ? `${w.toReview - w.uncategorized} more to check` : "",
                ]
                  .filter(Boolean)
                  .join("; ")}
                .
              </Caption1>
            )}
          </div>
        );
      })}
    </Card>
  );
}

function TimeOffCard({ d }: { d: HomeResponse }) {
  const t = d.timeOff;
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>Time off and pay dates</Subtitle2>
        {t && (
          <Button size="small" onClick={() => go("timeoff", "mine")}>
            Ask for time off
          </Button>
        )}
      </div>
      <div className="kpis">
        {t && <Kpi label="Vacation available" value={h(t.vacation.available)} sub={t.vacation.estimated ? "estimated until payroll's figure is in" : `of ${h(t.vacation.balance)} today`} />}
        {t && <Kpi label="Personal / sick available" value={h(t.personal.available)} sub={t.personal.estimated ? "estimated" : `of ${h(t.personal.balance)} today`} />}
        {d.payday && <Kpi label="Next payday" value={d.payday} sub={daysUntil(d.today!, d.payday) === 0 ? "today" : `in ${daysUntil(d.today!, d.payday)} days`} />}
      </div>
      {t && t.upcoming.length > 0 && (
        <Caption1 className="block">
          Booked: {t.upcoming.map((u) => `${BUCKET_LABEL[u.bucket].toLowerCase()} ${u.from === u.to ? u.from : `${u.from} to ${u.to}`} (${h(u.hours)})`).join("; ")}.
        </Caption1>
      )}
      {t && t.pending > 0 && (
        <Caption1 className="block">
          {t.pending} {t.pending === 1 ? "request" : "requests"} waiting for a decision.
        </Caption1>
      )}
      {d.nextHolidays && d.nextHolidays.length > 0 && <Caption1 className="block muted">Company holidays: {d.nextHolidays.map((x) => `${x.name} ${x.day}`).join(", ")}.</Caption1>}
      {!t && <Caption1 className="block muted">1099 providers have no time-off balances.</Caption1>}
    </Card>
  );
}

function AheadCard({ ahead }: { ahead: NonNullable<HomeResponse["ahead"]> }) {
  const { label } = useCodeLabels();
  return (
    <Card className="panel">
      <Subtitle2>The next 4 weeks</Subtitle2>
      <table className="edit-table compact">
        <thead>
          <tr>
            <th />
            {ahead.weekStarts.map((w) => (
              <th key={w} className="num">
                {w.slice(5)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ahead.projects.map((p) => (
            <tr key={p.code}>
              <td>{label(p.code)}</td>
              {p.hours.map((x, i) => (
                <td key={i} className="num">
                  {x ? h(x) : ""}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td>
              <strong>Planned of capacity</strong>
            </td>
            {ahead.planned.map((x, i) => {
              const cap = ahead.capacity[i];
              const load = cap > 0 ? x / cap : null;
              return (
                <td key={i} className={`num${load !== null && x > 0 ? ` load-${statusOf(load, ahead.thresholds)}` : ""}`}>
                  {h(x)} / {h(cap)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      {!ahead.projects.length && <Caption1 className="muted">Nothing planned for you on booked or pending projects.</Caption1>}
    </Card>
  );
}

function YtdCard({ ytd }: { ytd: NonNullable<HomeResponse["ytd"]> }) {
  const { label } = useCodeLabels();
  return (
    <Card className="panel">
      <Subtitle2>This year against plan</Subtitle2>
      <div className="kpis">
        <Kpi label="On projects" value={h(ytd.onProjects)} sub={`against ${h(ytd.planned)} planned to date`} />
        <Kpi label="Billable utilization" value={ytd.billableUtilization === null ? "—" : pct(ytd.billableUtilization, 0)} sub={`billable hours of ${h(ytd.expected)} expected`} />
        <Kpi label="All hours reported" value={h(ytd.reported)} sub="every code, time off included" />
      </div>
      {ytd.projects.length > 0 && (
        <table className="edit-table compact">
          <thead>
            <tr>
              <th>Project</th>
              <th className="num">Planned</th>
              <th className="num">Reported</th>
            </tr>
          </thead>
          <tbody>
            {ytd.projects.map((p) => (
              <tr key={p.code}>
                <td>
                  {label(p.code)}
                  {!p.billable && <Caption1 className="muted"> (internal)</Caption1>}
                </td>
                <td className="num">{h(p.planned)}</td>
                <td className="num">{h(p.reported)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/** Their own pay and what it costs in full: everyone sees their own (Chris, 2026-10-15). */
function PayCard({ pay }: { pay: NonNullable<HomeResponse["pay"]> }) {
  const flat = isFlatPay(pay.type as PayType);
  return (
    <Card className="panel wide">
      <Subtitle2>Your pay and total compensation</Subtitle2>
      <Caption1 className="block">What the company spends on you each year: your pay, the employer's taxes on it, and your benefits and other costs.</Caption1>
      <div className="kpis">
        <Kpi label="Pay" value={money(pay.pay)} sub="a year" />
        <Kpi label="Employer taxes" value={money(pay.taxes)} sub="Social Security, Medicare, unemployment" />
        <Kpi label="Benefits and costs" value={money(pay.costs)} sub="healthcare, equipment and the like" />
        <Kpi label="Total, before bonus" value={money(pay.totalBase)} sub={`${perHour(pay.baseRate)} over your productive hours`} />
        {pay.bonusTarget > 0 && <Kpi label="Target bonus" value={money(pay.bonusTarget)} sub={`total with bonus ${money(pay.totalOte)}`} />}
        <Kpi label="Bonus progress" value="TBD" sub="measured at year end for now" />
        {flat && pay.actualRateYtd !== null && <Kpi label="Your cost per hour this year" value={perHour(pay.actualRateYtd)} sub={`${money(pay.costYtd)} over the hours you reported`} />}
      </div>
      <details>
        <summary>
          <Caption1>Line by line</Caption1>
        </summary>
        <table className="edit-table compact">
          <tbody>
            {pay.components.map((c) => (
              <tr key={c.key}>
                <td>{c.label}</td>
                <td className="num">{money(c.annual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      {pay.taxes === 0 && pay.type.startsWith("contractor") && <Caption1 className="muted">As a 1099 provider, no employer taxes apply.</Caption1>}
      <Caption1 className="block muted">Visible only to you and admins.</Caption1>
    </Card>
  );
}
